// router/guards.js
import { parsePermission } from '../casl/utils/permissionParser'
import { canNavigate } from "@layouts/plugins/casl"
import { useCookie } from '@core/composable/useCookie'
import { computed } from 'vue'
import { useAbility } from '../casl/composables/useAbility'

export const setupGuards = router => {
  router.beforeEach(to => {
    // 1. Allow public routes (login, etc.)
    if (to.meta.public) {
      return true; // Explicitly return true to continue without redirection
    }

    // 2. Check authentication
    const isLoggedIn = !!(useCookie('userData').value && useCookie('accessToken').value)
    
    if (!isLoggedIn) {
      if (to.name === 'login') {
        return true; // Already going to login, continue
      }
      
      // Not authenticated and not going to login, redirect to login
      return { 
        name: 'login',
        // Only keep path in query if it's meaningful
        query: to.path !== '/' ? { to: to.path } : {} 
      }
    }
    
    // 3. User is authenticated, check permissions

    // Role-based check. Superadmin always passes — they own everything.
    // Users can have multiple roles (e.g. mobile-admin + superadmin); the
    // cookie only carries the first one for size reasons, so we look up
    // the full role list from localStorage.fullUserData and fall back to
    // the cookie's single role.
    if (to.meta.role) {
      let userRoles = []
      try {
        const raw = localStorage.getItem('fullUserData')
        if (raw) {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed?.roles)) {
            userRoles = parsed.roles
              .map(r => (typeof r === 'string' ? r : r?.name))
              .filter(Boolean)
          } else if (parsed?.role) {
            userRoles = [parsed.role]
          }
        }
      } catch (_) { /* ignore — fall back to cookie */ }

      if (!userRoles.length) {
        const cookieRole = useCookie('userData').value?.role
        if (cookieRole) userRoles = [cookieRole]
      }

      const allowedRoles = Array.isArray(to.meta.role) ? to.meta.role : [to.meta.role]
      const hasRole = userRoles.includes('superadmin') || userRoles.some(r => allowedRoles.includes(r))

      if (!hasRole) {
        return { name: 'not-authorized' }
      }
    }
    
    // CASL permission check
    if (to.meta.action && to.meta.subject) {
      const { ability } = useAbility()
      
      // Simple permission check - user must have the exact permission
      const canAccess = ability.can(to.meta.action, to.meta.subject)
      
      if (!canAccess) {
        return { name: 'not-authorized' }
      }
    }
    
    // Team-based permissions check (for array of permissions)
    if (to.meta.permissions && Array.isArray(to.meta.permissions)) {
      const { ability } = useAbility()
      
      // First check if user has global manage permission (superadmin access)
      if (ability.can('manage', 'all')) {
        return true // Superadmin can access everything
      }
      
      // Check if user has ANY of the listed permissions (OR logic)
      const hasAnyPermission = to.meta.permissions.some(permission => {
        // Parse permission string like 'view-GB-parcels' into action and subject
        if (permission.includes('-') && permission.match(/-(GB|GD|JP)-/)) {
          const [action, team, ...subjectParts] = permission.split('-')
          const subject = subjectParts.join('')
          
          if (action && team && subject) {
            // Check team-based permission: action on TeamSubject (e.g., 'view' on 'GBParcels')
            const teamSubject = `${team.toUpperCase()}${subject.charAt(0).toUpperCase() + subject.slice(1)}`
            return ability.can(action, teamSubject)
          }
        }
        
        // Fallback to direct permission check
        return ability.can(permission)
      })
      
      if (!hasAnyPermission) {
        return { name: 'not-authorized' }
      }
    }
    
    // No redirects needed, continue navigation
    return true
  })
}